import { useCallback, useRef, useState } from 'react';
import {
  AdminHarborRunnerToken,
  listAdminHarborRunnerTokens,
} from '../../../../services/harborRunnerTokens';
import {
  cancelHarborJob,
  deleteHarborJob,
  getHarborJobArtifactDownload,
  HarborArtifactDownload,
  HarborJobArtifactId,
  HarborJob,
  listHarborJobs,
} from '../../../../services/harborRuns';
import { logGlobalError } from '../../../../utils/globalErrorLogger';

const PAGE_SIZE = 50;

export interface HarborRunnersToolViewModel {
  busy: boolean;
  loading: boolean;
  error: string | null;
  runners: AdminHarborRunnerToken[];
  runnerPage: number;
  runnersHasMore: boolean;
  jobs: HarborJob[];
  jobsPage: number;
  jobsHasMore: boolean;
  jobActionId: string | null;
  initialize: () => Promise<void>;
  refresh: () => Promise<void>;
  nextRunnerPage: () => void;
  previousRunnerPage: () => void;
  nextJobsPage: () => void;
  previousJobsPage: () => void;
  cancelJob: (jobId: string) => Promise<void>;
  deleteJob: (jobId: string) => Promise<void>;
  downloadArtifact: (
    jobId: string,
    artifactId: HarborJobArtifactId,
  ) => Promise<HarborArtifactDownload | null>;
}

export function useHarborRunnersToolViewModel(): HarborRunnersToolViewModel {
  const [busy, setBusy] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [runners, setRunners] = useState<AdminHarborRunnerToken[]>([]);
  const [runnerPage, setRunnerPage] = useState(0);
  const [runnersHasMore, setRunnersHasMore] = useState(false);
  const [jobs, setJobs] = useState<HarborJob[]>([]);
  const [jobsPage, setJobsPage] = useState(0);
  const [jobsHasMore, setJobsHasMore] = useState(false);
  const [jobActionId, setJobActionId] = useState<string | null>(null);
  const runnerCursorsRef = useRef<Array<string | null>>([null]);
  const jobCursorsRef = useRef<Array<string | null>>([null]);
  const requestSeqRef = useRef(0);

  const loadFirstPage = useCallback(async (initial: boolean) => {
    const seq = ++requestSeqRef.current;
    if (initial) setBusy(true);
    setLoading(true);
    setError(null);
    runnerCursorsRef.current = [null];
    jobCursorsRef.current = [null];
    setRunnerPage(0);
    setJobsPage(0);
    try {
      const [nextRunners, nextJobs] = await Promise.all([
        listAdminHarborRunnerTokens(),
        listHarborJobs(),
      ]);
      if (seq !== requestSeqRef.current) return;
      setRunners(nextRunners.tokens);
      setRunnersHasMore(Boolean(nextRunners.nextPageToken));
      if (nextRunners.nextPageToken) runnerCursorsRef.current[1] = nextRunners.nextPageToken;
      setJobs(nextJobs.jobs);
      setJobsHasMore(Boolean(nextJobs.nextPageToken));
      if (nextJobs.nextPageToken) jobCursorsRef.current[1] = nextJobs.nextPageToken;
    } catch (nextError: any) {
      if (seq !== requestSeqRef.current) return;
      setError(nextError?.message ?? 'Failed to load Harbor runners and jobs.');
      logGlobalError(nextError, { context: 'HarborRunnersTool.loadFirstPage' });
    } finally {
      if (seq !== requestSeqRef.current) return;
      setLoading(false);
      setBusy(false);
    }
  }, []);

  const loadRunnerPage = useCallback(async (targetPage: number) => {
    const pageToken = runnerCursorsRef.current[targetPage] ?? null;
    if (targetPage > 0 && !pageToken) return;
    const seq = ++requestSeqRef.current;
    setLoading(true);
    setError(null);
    try {
      const result = await listAdminHarborRunnerTokens(pageToken);
      if (seq !== requestSeqRef.current) return;
      setRunners(result.tokens);
      setRunnerPage(targetPage);
      setRunnersHasMore(Boolean(result.nextPageToken));
      runnerCursorsRef.current = runnerCursorsRef.current.slice(0, targetPage + 1);
      if (result.nextPageToken) runnerCursorsRef.current[targetPage + 1] = result.nextPageToken;
    } catch (nextError: any) {
      if (seq !== requestSeqRef.current) return;
      setError(nextError?.message ?? 'Failed to load Harbor runners.');
      logGlobalError(nextError, {
        context: 'HarborRunnersTool.loadRunnerPage',
        metadata: { page: targetPage },
      });
    } finally {
      if (seq === requestSeqRef.current) setLoading(false);
    }
  }, []);

  const loadJobsPage = useCallback(async (targetPage: number) => {
    const pageToken = jobCursorsRef.current[targetPage] ?? null;
    if (targetPage > 0 && !pageToken) return;
    const seq = ++requestSeqRef.current;
    setLoading(true);
    setError(null);
    try {
      const result = await listHarborJobs(pageToken);
      if (seq !== requestSeqRef.current) return;
      setJobs(result.jobs);
      setJobsPage(targetPage);
      setJobsHasMore(Boolean(result.nextPageToken));
      jobCursorsRef.current = jobCursorsRef.current.slice(0, targetPage + 1);
      if (result.nextPageToken) jobCursorsRef.current[targetPage + 1] = result.nextPageToken;
    } catch (nextError: any) {
      if (seq !== requestSeqRef.current) return;
      setError(nextError?.message ?? 'Failed to load Harbor jobs.');
      logGlobalError(nextError, {
        context: 'HarborRunnersTool.loadJobsPage',
        metadata: { page: targetPage },
      });
    } finally {
      if (seq === requestSeqRef.current) setLoading(false);
    }
  }, []);

  const initialize = useCallback(() => loadFirstPage(true), [loadFirstPage]);
  const refresh = useCallback(() => loadFirstPage(false), [loadFirstPage]);

  const nextRunnerPage = useCallback(() => {
    if (!runnersHasMore) return;
    void loadRunnerPage(runnerPage + 1);
  }, [loadRunnerPage, runnerPage, runnersHasMore]);

  const previousRunnerPage = useCallback(() => {
    if (runnerPage === 0) return;
    void loadRunnerPage(runnerPage - 1);
  }, [loadRunnerPage, runnerPage]);

  const nextJobsPage = useCallback(() => {
    if (!jobsHasMore) return;
    void loadJobsPage(jobsPage + 1);
  }, [jobsHasMore, jobsPage, loadJobsPage]);

  const previousJobsPage = useCallback(() => {
    if (jobsPage === 0) return;
    void loadJobsPage(jobsPage - 1);
  }, [jobsPage, loadJobsPage]);

  const cancelJob = useCallback(async (jobId: string) => {
    setJobActionId(jobId);
    setError(null);
    try {
      await cancelHarborJob(jobId);
      await loadJobsPage(jobsPage);
    } catch (nextError: any) {
      setError(nextError?.message ?? 'Failed to cancel the Harbor job.');
      logGlobalError(nextError, {
        context: 'HarborRunnersTool.cancelJob',
        metadata: { jobId },
      });
    } finally {
      setJobActionId(null);
    }
  }, [jobsPage, loadJobsPage]);

  const deleteJob = useCallback(async (jobId: string) => {
    setJobActionId(jobId);
    setError(null);
    try {
      await deleteHarborJob(jobId);
      await loadJobsPage(jobsPage);
    } catch (nextError: any) {
      setError(nextError?.message ?? 'Failed to delete the Harbor job.');
      logGlobalError(nextError, {
        context: 'HarborRunnersTool.deleteJob',
        metadata: { jobId },
      });
    } finally {
      setJobActionId(null);
    }
  }, [jobsPage, loadJobsPage]);

  const downloadArtifact = useCallback(async (
    jobId: string,
    artifactId: HarborJobArtifactId,
  ): Promise<HarborArtifactDownload | null> => {
    setJobActionId(jobId);
    setError(null);
    try {
      return await getHarborJobArtifactDownload(jobId, artifactId);
    } catch (nextError: any) {
      setError(nextError?.message ?? 'Failed to prepare the Harbor artifact download.');
      logGlobalError(nextError, {
        context: 'HarborRunnersTool.downloadArtifact',
        metadata: { jobId, artifactId },
      });
      return null;
    } finally {
      setJobActionId(null);
    }
  }, []);

  return {
    busy,
    loading,
    error,
    runners,
    runnerPage,
    runnersHasMore,
    jobs,
    jobsPage,
    jobsHasMore,
    jobActionId,
    initialize,
    refresh,
    nextRunnerPage,
    previousRunnerPage,
    nextJobsPage,
    previousJobsPage,
    cancelJob,
    deleteJob,
    downloadArtifact,
  };
}

export { PAGE_SIZE as HARBOR_ADMIN_PAGE_SIZE };
